import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Receipt, Plus, Trash2, FileText, Download, Send, Building2, Upload, AlertTriangle, Link2, BookMarked, Pencil, X, Check, Bell, BellOff, Eye, Repeat } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = {
  unpaid: { label: "Unpaid", cls: "bg-[#FFC43D]/20 text-[#946200] border-[#FFC43D]/50" },
  paid_cash: { label: "Paid via Cash", cls: "bg-[#2EC4B6]/15 text-[#0d7d72] border-[#2EC4B6]/40" },
  paid_credit: { label: "Paid via Credit", cls: "bg-[#2563EB]/15 text-[#1d4ed8] border-[#2563EB]/40" },
};
const emptyLine = () => ({ service_code: "", description: "", qty: 1, unit_price: 0 });
const plus30 = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); };
const DEFAULT_MEMO = "Thank you for your business.";
const buyCredits = async (pack) => {
  try {
    const { data } = await api.post("/billing/credits/checkout", { pack, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
    return true;
  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
};


export default function InvoiceManager() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientName, setClientName] = useState("");
  const [clientId, setClientId] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [status, setStatus] = useState("unpaid");
  const [dueDate, setDueDate] = useState(plus30());
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sendOnCreate, setSendOnCreate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [lineModalOpen, setLineModalOpen] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [docType, setDocType] = useState("invoice");
  const [invoiceType, setInvoiceType] = useState("business");
  const [recurrence, setRecurrence] = useState("none");
  const [editing, setEditing] = useState(null);
  const [credits, setCredits] = useState(null);
  const [downPayment, setDownPayment] = useState("");
  const [memo, setMemo] = useState(DEFAULT_MEMO);
  const loadCredits = useCallback(() => api.get("/billing/credits").then((r) => setCredits(r.data)).catch(() => {}), []);

  const load = useCallback(async () => {
    try {
      const [inv, cl, tpl] = await Promise.all([api.get("/invoices"), api.get("/clients"), api.get("/line-item-templates")]);
      setInvoices(inv.data); setClients(cl.data); setTemplates(tpl.data);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); loadCredits(); }, [load, loadCredits]);
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("credits_session");
    if (!sid) return;
    url.searchParams.delete("credits_session");
    window.history.replaceState({}, "", url.toString());
    (async () => {
      for (let i = 0; i < 8; i++) {
        try {
          const { data } = await api.get(`/billing/credits/status/${sid}`);
          if (data.paid) { toast.success(`Credits added — you now have ${data.credits} invoice credits`); loadCredits(); return; }
        } catch (e) { return; }
        await new Promise((r) => setTimeout(r, 1800));
      }
      toast.warning("Payment is still processing — your credits will appear shortly");
    })();
  }, [loadCredits]);
  const loadTemplates = useCallback(() => api.get("/line-item-templates").then((r) => setTemplates(r.data)).catch(() => {}), []);

  const total = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const dpAmt = Math.min(Math.max(Number(downPayment) || 0, 0), total);
  const balanceDue = total - dpAmt;
  const invoiceDocs = invoices.filter((i) => (i.doc_type || "invoice") !== "estimate");
  const estimateDocs = invoices.filter((i) => (i.doc_type || "invoice") === "estimate");
  const convert = async (est) => {
    try { await api.post(`/invoices/${est.id}/convert`); toast.success(`Converted ${est.number || "estimate"} to an invoice`); load(); loadCredits(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const delLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
  const addFromModal = (line) => setLines((ls) => {
    const only = ls.length === 1 && !ls[0].description && !Number(ls[0].unit_price);
    return only ? [line] : [...ls, line];
  });

  const pickClient = (id) => {
    setClientId(id);
    const c = clients.find((x) => x.id === id);
    if (c) setClientName(c.name);
  };

  const autoSendOnCreate = async (created) => {
    const client = clients.find((c) => c.id === created.client_id);
    let to = client?.email || "";
    if (!to) to = (window.prompt(`Email invoice ${created.number || ""} to:`, "") || "").trim();
    if (!to || !to.includes("@")) return toast.warning("Invoice saved, but no client email to send to");
    try {
      const { data } = await api.post(`/invoices/${created.id}/send`, { to });
      if (data.status === "sent") toast.success(`Invoice emailed to ${to}`);
      else toast.error(`Saved, but email failed: ${data.detail || "delivery error"}`);
    } catch (e) { toast.error("Saved, but email could not be sent"); }
  };

  const resetForm = () => {
    setClientName(""); setClientId(""); setLines([emptyLine()]); setStatus("unpaid"); setDueDate(plus30()); setRecurrence("none"); setEditing(null); setDownPayment(""); setMemo(DEFAULT_MEMO);
  };
  const startEdit = (inv) => {
    setEditing(inv);
    setDocType(inv.doc_type || "invoice"); setInvoiceType(inv.invoice_type || "business");
    setClientId(inv.client_id || ""); setClientName(inv.client_name || "");
    setLines((inv.line_items || []).length ? inv.line_items.map((l) => ({ service_code: l.service_code || "", description: l.description || "", qty: l.qty, unit_price: l.unit_price })) : [emptyLine()]);
    setDueDate(inv.due_date || plus30());
    setRecurrence(inv.recur_active ? inv.recurrence : "none");
    setDownPayment(inv.down_payment ? String(inv.down_payment) : ""); setMemo(inv.memo ?? "");
    window.scrollTo({ top: document.querySelector('[data-testid="invoice-manager"]')?.offsetTop || 0, behavior: "smooth" });
  };
  const saveEdit = async () => {
    const body = { client_id: clientId, client_name: clientName.trim(), line_items: lines, due_date: dueDate, invoice_type: invoiceType, down_payment: dpAmt, memo };
    if (editing.doc_type !== "estimate" && !editing.recur_parent) body.recurrence = recurrence;
    await api.patch(`/invoices/${editing.id}`, body);
    toast.success(`${editing.number || "Invoice"} updated`);
  };

  const create = async () => {
    if (!clientName.trim()) return toast.error("Choose or enter a client");
    setBusy(true);
    try {
      if (editing) { await saveEdit(); resetForm(); load(); return; }
      const { data: created } = await api.post("/invoices", { client_id: clientId, client_name: clientName.trim(), line_items: lines, status, due_date: dueDate, doc_type: docType, invoice_type: invoiceType, recurrence: docType === "invoice" ? recurrence : "none", recur_auto_send: true, down_payment: dpAmt, memo });
      toast.success(docType === "estimate" ? "Estimate created" : (status === "unpaid" ? "Invoice created" : "Invoice created & added to Business Income"));
      if (docType !== "estimate" && credits && !credits.unlimited && credits.credits === 1) {
        toast.warning("That was your last invoice credit", { description: "Top up now so your next invoice isn't blocked.", action: { label: "Top up 5 · $4", onClick: () => buyCredits("5") }, duration: 10000 });
      }
      if (sendOnCreate && docType !== "estimate") await autoSendOnCreate(created);
      resetForm();
      load(); loadCredits();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); if (e.response?.status === 402) loadCredits(); }
    finally { setBusy(false); }
  };

  const changeStatus = async (inv, st) => {
    try {
      await api.patch(`/invoices/${inv.id}`, { status: st });
      if (st !== "unpaid") toast.success(`Added ${money(inv.total)} from ${inv.client_name} to Business Income`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleReminders = async (inv) => {
    try {
      await api.patch(`/invoices/${inv.id}`, { reminders_enabled: !inv.reminders_enabled });
      toast.success(inv.reminders_enabled ? "Auto reminders paused" : "Auto reminders on (1, 7 & 14 days overdue)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleRecurring = async (inv) => {
    try {
      const { data } = await api.patch(`/invoices/${inv.id}`, { recur_active: !inv.recur_active });
      toast.success(inv.recur_active ? "Recurring schedule stopped" : `Recurring resumed — next invoice ${data.recur_next_date}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number || ""} for ${inv.client_name}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); load(); } catch (e) {}
  };

  const [preview, setPreview] = useState(null);
  const showPdf = (data, title, extra, pages) => setPreview({ url: URL.createObjectURL(new Blob([data], { type: "application/pdf" })), title, pages, ...extra });
  const closePreview = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };
  const downloadPdf = async (inv) => {
    try {
      const [{ data }, img] = await Promise.all([api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" }), api.get(`/invoices/${inv.id}/pdf?fmt=png`)]);
      showPdf(data, inv.number || "Invoice", { inv: (inv.doc_type || "invoice") === "invoice" && inv.status === "unpaid" ? inv : null }, img.data.pages);
    } catch (e) { toast.error("Could not open the invoice PDF"); }
  };
  const previewDraft = async () => {
    try {
      const body = { client_id: clientId, client_name: clientName.trim(), line_items: lines, status, due_date: dueDate, doc_type: docType, invoice_type: invoiceType, down_payment: dpAmt, memo, number: editing?.number || null };
      const [{ data }, img] = await Promise.all([api.post("/invoices/preview-pdf", body, { responseType: "blob" }), api.post("/invoices/preview-pdf?fmt=png", body)]);
      showPdf(data, editing?.number || `Draft ${docType}`, { draft: true }, img.data.pages);
    } catch (e) { toast.error("Could not build the preview"); }
  };

  const sendEmail = async (inv) => {
    const client = clients.find((c) => c.id === inv.client_id);
    let to = client?.email || "";
    to = window.prompt(`Email invoice ${inv.number || ""} to:`, to);
    if (to === null) return;
    to = (to || "").trim();
    if (!to || !to.includes("@")) return toast.error("Enter a valid email address");
    setSendingId(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/send`, { to });
      if (data.status === "sent") toast.success(`Invoice emailed to ${to}`);
      else toast.error(`Could not send: ${data.detail || "email delivery failed"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingId(null); }
  };

  const copyPayLink = (inv) => {
    const url = `${window.location.origin}/pay/${inv.id}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Payment link copied — share it with your client"))
      .catch(() => window.prompt("Copy this payment link:", url));
  };

  return (
    <div data-testid="invoice-manager" className="space-y-6">
      <div className="flex items-center gap-2" data-testid="billing-hub-title">
        <Receipt className="w-5 h-5 text-[var(--accent)]" />
        <h2 className="font-serif text-2xl tracking-tight">Billing Hub</h2>
      </div>
      {/* Business identity */}
      <BusinessProfilePanel open={profileOpen} setOpen={setProfileOpen} />
      <PdfPreviewDialog preview={preview} onClose={closePreview} onSend={(inv) => { closePreview(); sendEmail(inv); }} />
      <CreditsBar credits={credits} />

      {/* Creator */}
      <div data-testid="invoice-editor" className={`border bg-white p-5 ${editing ? "border-[var(--accent)]" : "border-[var(--line)]"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><Receipt className="w-4 h-4" /> {editing ? `Editing ${editing.number || docType}` : `New ${docType === "estimate" ? "estimate" : "invoice"}`}</p>
          <div className="flex items-center gap-2">
            <div className={`flex border border-[var(--line)] rounded overflow-hidden ${editing ? "opacity-50 pointer-events-none" : ""}`} data-testid="doc-type-toggle">
              {[["invoice", "Invoice"], ["estimate", "Estimate"]].map(([v, l]) => (
                <button key={v} data-testid={`doc-type-${v}`} onClick={() => setDocType(v)} className={`h-8 px-3 text-xs font-medium transition-colors ${docType === v ? "bg-[#1A1A18] text-white" : "bg-white text-[var(--text-2)]"}`}>{l}</button>
              ))}
            </div>
            <div className="flex border border-[var(--line)] rounded overflow-hidden" data-testid="invoice-type-toggle">
              {[["business", "Business"], ["personal", "Personal"]].map(([v, l]) => (
                <button key={v} data-testid={`invoice-type-${v}`} onClick={() => setInvoiceType(v)} className={`h-8 px-3 text-xs font-medium transition-colors ${invoiceType === v ? "bg-[var(--accent)] text-white" : "bg-white text-[var(--text-2)]"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select data-testid="invoice-client-select" value={clientId} onChange={(e) => pickClient(e.target.value)} className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]">
            <option value="">Select a client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input data-testid="invoice-client-name" value={clientName} onChange={(e) => { setClientName(e.target.value); setClientId(""); }} placeholder="or type client name" className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
        </div>

        <div className="mt-4 space-y-2">
          <div className="hidden sm:grid grid-cols-[110px_minmax(0,55fr)_64px_90px_90px_32px] gap-2 text-[10px] uppercase tracking-wider text-[var(--text-2)]">
            <span>Service Code</span><span>Description</span><span className="text-right">Qty</span><span className="text-right">Unit $</span><span className="text-right">Amount</span><span></span>
          </div>
          {lines.map((l, i) => (
            <div key={i} data-testid={`invoice-line-${i}`} className="grid grid-cols-[minmax(0,1fr)_56px_80px_72px_28px] sm:grid-cols-[110px_minmax(0,55fr)_64px_90px_90px_32px] gap-2 items-start border border-[var(--line)] sm:border-0 p-2 sm:p-0">
              <input data-testid={`invoice-line-code-${i}`} value={l.service_code || ""} onChange={(e) => setLine(i, { service_code: e.target.value })} placeholder="Code" className="h-10 px-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
              <textarea data-testid={`invoice-line-desc-${i}`} value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} rows={2} placeholder="Item / service description" className="col-span-5 order-last sm:order-none sm:col-span-1 min-h-10 px-2 py-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)] resize-y leading-snug" />
              <input data-testid={`invoice-line-qty-${i}`} type="number" value={l.qty} onChange={(e) => setLine(i, { qty: parseFloat(e.target.value) || 0 })} className="h-10 px-2 border border-[var(--line)] bg-white text-sm text-right outline-none focus:border-[var(--accent)]" />
              <input data-testid={`invoice-line-price-${i}`} type="number" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: parseFloat(e.target.value) || 0 })} className="h-10 px-2 border border-[var(--line)] bg-white text-sm text-right outline-none focus:border-[var(--accent)]" />
              <span className="text-sm font-mono text-right self-center">{money((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</span>
              <button data-testid={`invoice-line-remove-${i}`} onClick={() => delLine(i)} className="text-[var(--text-2)] hover:text-[var(--danger)] justify-self-center self-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex items-center gap-4 pt-1">
            <button data-testid="invoice-add-line" onClick={() => setLineModalOpen(true)} className="inline-flex items-center gap-1.5 text-xs text-[var(--accent)] hover:opacity-80"><Plus className="w-3.5 h-3.5" /> Add line item</button>
            <button data-testid="invoice-lib-toggle" onClick={() => setLibOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-xs text-[var(--text-2)] hover:text-[var(--accent)]"><BookMarked className="w-3.5 h-3.5" /> Saved items library</button>
          </div>
          {libOpen && <SavedItemsLibrary templates={templates} reload={loadTemplates} onInsert={addFromModal} />}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="label-mono text-[var(--text-2)]">Status</span>
              <select data-testid="invoice-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]">
                <option value="unpaid">Unpaid</option>
                <option value="paid_cash">Paid via Cash</option>
                <option value="paid_credit">Paid via Credit</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="label-mono text-[var(--text-2)]">Due</span>
              <input data-testid="invoice-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-9 px-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
            </div>
            {docType === "invoice" && !editing?.recur_parent && (
              <div className="flex items-center gap-2">
                <span className="label-mono text-[var(--text-2)] flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> Repeat</span>
                <select data-testid="invoice-recurrence" value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="h-9 px-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]">
                  <option value="none">One-time</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
            )}
            {!editing && <label data-testid="invoice-send-on-create" className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={sendOnCreate} onChange={(e) => setSendOnCreate(e.target.checked)} className="w-4 h-4 accent-[var(--accent)]" />
              <span className="text-sm text-[var(--text-2)] flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Email to client on create</span>
            </label>}
          </div>
          <TotalsBlock total={total} downPayment={downPayment} setDownPayment={setDownPayment} dpAmt={dpAmt} balanceDue={balanceDue} />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <label className="block">
            <span className="label-mono text-[var(--text-2)] text-[11px]">Memo / Note to Client</span>
            <textarea data-testid="invoice-memo" value={memo} onChange={(e) => setMemo(e.target.value)} rows={2} placeholder="Optional note shown below the total" className="mt-1 w-full px-3 py-2 border border-[var(--line)] bg-white text-sm italic outline-none focus:border-[var(--accent)] resize-y" />
          </label>
          <div className="flex items-center gap-3 justify-end">
            <button data-testid="invoice-preview-draft" onClick={previewDraft} className="h-11 px-4 border border-[var(--line)] text-sm flex items-center gap-1.5 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"><Eye className="w-4 h-4" /> Preview PDF</button>
            {editing && <button data-testid="invoice-edit-cancel" onClick={resetForm} className="h-11 px-4 border border-[var(--line)] text-sm hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors">Cancel</button>}
            <button data-testid="invoice-create" onClick={create} disabled={busy} className="h-11 px-5 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors disabled:opacity-50">{busy ? "Saving…" : editing ? "Save changes" : (docType === "estimate" ? "Create estimate" : "Create invoice")}</button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="border border-[var(--line)] bg-white p-5">
        <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-4"><FileText className="w-4 h-4" /> Invoices</p>
        {invoiceDocs.length === 0 ? (
          <p className="text-sm text-[var(--text-2)]">No invoices yet.</p>
        ) : (
          <div className="space-y-2">
            {invoiceDocs.map((inv) => {
              const s = STATUS[inv.status] || STATUS.unpaid;
              return (
                <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className={`flex flex-wrap items-center gap-3 border p-3 ${inv.overdue ? "border-[var(--danger)]/50 bg-[var(--danger)]/5" : "border-[var(--line)] bg-[#f9f9f7]"}`}>
                  {inv.overdue ? (
                    <span data-testid={`invoice-overdue-${inv.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-[var(--danger)]/15 text-[var(--danger)] border-[var(--danger)]/50 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Overdue</span>
                  ) : (
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
                  )}
                  <div className="min-w-0 flex-1 basis-[calc(100%-7rem)] sm:basis-auto">
                    <p className="text-sm font-medium truncate">{inv.number ? `${inv.number} · ` : ""}{inv.client_name}</p>
                    <p className="text-xs text-[var(--text-2)]">{(inv.line_items || []).length} item(s) · issued {inv.issued_date}{inv.due_date ? ` · due ${inv.due_date}` : ""}</p>
                    <LineSummary inv={inv} />
                    <InvoiceActivity inv={inv} />
                    <RecurringBadge inv={inv} onToggle={() => toggleRecurring(inv)} />
                  </div>
                  <span className="font-mono text-sm text-right">{money(inv.down_payment > 0 ? inv.balance_due : inv.total)}{inv.down_payment > 0 && <span data-testid={`invoice-row-dp-${inv.id}`} className="block text-[10px] text-[var(--text-2)]">of {money(inv.total)} · {money(inv.down_payment)} down</span>}</span>
                  <select data-testid={`invoice-status-${inv.id}`} value={inv.status} onChange={(e) => changeStatus(inv, e.target.value)} className="h-9 px-2 border border-[var(--line)] bg-white text-xs outline-none focus:border-[var(--accent)]">
                    <option value="unpaid">Unpaid</option>
                    <option value="paid_cash">Paid via Cash</option>
                    <option value="paid_credit">Paid via Credit</option>
                  </select>
                  <button data-testid={`invoice-edit-${inv.id}`} onClick={() => startEdit(inv)} disabled={inv.status !== "unpaid"} title={inv.status !== "unpaid" ? "Paid invoices are locked — set to Unpaid to edit" : "Edit invoice"} className="w-9 h-9 flex items-center justify-center border border-[var(--line)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors disabled:opacity-30"><Pencil className="w-4 h-4" /></button>
                  {inv.status === "unpaid" && (
                    <button data-testid={`invoice-reminders-toggle-${inv.id}`} onClick={() => toggleReminders(inv)} title={inv.reminders_enabled ? "Auto reminders on — click to pause" : "Auto reminders paused — click to turn on"} className={`w-9 h-9 flex items-center justify-center border transition-colors ${inv.reminders_enabled ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--line)] text-[var(--text-2)]"} hover:border-[var(--accent)]`}>{inv.reminders_enabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}</button>
                  )}
                  {inv.status === "unpaid" && (
                    <button data-testid={`invoice-paylink-${inv.id}`} onClick={() => copyPayLink(inv)} title="Copy online payment link" className="w-9 h-9 flex items-center justify-center border border-[var(--line)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"><Link2 className="w-4 h-4" /></button>
                  )}
                  <button data-testid={`invoice-pdf-${inv.id}`} onClick={() => downloadPdf(inv)} title="Preview PDF" className="w-9 h-9 flex items-center justify-center border border-[var(--line)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"><FileText className="w-4 h-4" /></button>
                  <button data-testid={`invoice-send-${inv.id}`} onClick={() => sendEmail(inv)} disabled={sendingId === inv.id} title="Email to client" className="w-9 h-9 flex items-center justify-center border border-[var(--line)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors disabled:opacity-50"><Send className="w-4 h-4" /></button>
                  <button data-testid={`invoice-delete-${inv.id}`} onClick={() => remove(inv)} className="w-9 h-9 flex items-center justify-center text-[var(--text-2)] hover:text-[var(--danger)]"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div data-testid="estimates-section" className="border border-[var(--line)] bg-white p-5">
        <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-4"><FileText className="w-4 h-4" /> Estimates</p>
        {estimateDocs.length === 0 ? (
          <p className="text-sm text-[var(--text-2)]">No estimates yet. Toggle “Estimate” above to create one.</p>
        ) : (
          <div className="space-y-2">
            {estimateDocs.map((inv) => (
              <div key={inv.id} data-testid={`estimate-row-${inv.id}`} className="flex flex-wrap items-center gap-3 border border-[var(--line)] bg-[#f9f9f7] p-3">
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-[#2563EB]/10 text-[#1d4ed8] border-[#2563EB]/40">Estimate</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{inv.number ? `${inv.number} · ` : ""}{inv.client_name}</p>
                  <p className="text-xs text-[var(--text-2)]">{(inv.line_items || []).length} item(s){inv.converted_to ? " · converted ✓" : ""}</p>
                  <LineSummary inv={inv} />
                </div>
                <span className="font-mono text-sm">{money(inv.total)}</span>
                <button data-testid={`estimate-edit-${inv.id}`} onClick={() => startEdit(inv)} disabled={!!inv.converted_to} title="Edit estimate" className="w-9 h-9 flex items-center justify-center border border-[var(--line)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors disabled:opacity-30"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`estimate-convert-${inv.id}`} onClick={() => convert(inv)} disabled={!!inv.converted_to} className="h-9 px-3 border border-[var(--accent)] text-[var(--accent)] text-xs hover:bg-[var(--accent)] hover:text-white transition-colors disabled:opacity-40 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Convert to Invoice</button>
                <button data-testid={`estimate-pdf-${inv.id}`} onClick={() => downloadPdf(inv)} title="Preview PDF" className="w-9 h-9 flex items-center justify-center border border-[var(--line)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"><FileText className="w-4 h-4" /></button>
                <button data-testid={`estimate-delete-${inv.id}`} onClick={() => remove(inv)} title="Delete" className="w-9 h-9 flex items-center justify-center border border-[var(--line)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <LineItemModal
        open={lineModalOpen}
        onClose={() => setLineModalOpen(false)}
        templates={templates}
        reloadTemplates={loadTemplates}
        onAdd={addFromModal}
      />
    </div>
  );
}

function BusinessProfilePanel({ open, setOpen }) {
  const [prof, setProf] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/business/profile").then((r) => setProf(r.data)).catch(() => {}); }, []);

  const set = (patch) => setProf((p) => ({ ...p, ...patch }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2MB");
    const reader = new FileReader();
    reader.onload = () => set({ business_logo: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/business/profile", prof);
      setProf(data);
      toast.success("Business details saved — they'll appear on your invoices");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!prof) return null;

  return (
    <div className="border border-[var(--line)] bg-white">
      <button data-testid="invoice-profile-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left">
        <span className="label-mono text-[var(--text-2)] flex items-center gap-2"><Building2 className="w-4 h-4" /> Business identity & logo</span>
        <span className="text-xs text-[var(--text-2)]">{open ? "Hide" : "Edit"}</span>
      </button>
      {open && (
        <div className="border-t border-[var(--line)] p-5 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-24 h-16 border border-dashed border-[var(--line)] bg-[#f9f9f7] flex items-center justify-center overflow-hidden shrink-0">
              {prof.business_logo ? <img src={prof.business_logo} alt="logo" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-[var(--text-2)]">No logo</span>}
            </div>
            <div className="flex items-center gap-2">
              <label data-testid="invoice-logo-upload" className="inline-flex items-center gap-1.5 h-9 px-3 border border-[var(--line)] text-sm cursor-pointer hover:border-[var(--accent)] transition-colors">
                <Upload className="w-4 h-4" /> Upload logo
                <input type="file" accept="image/*" onChange={onLogo} className="hidden" />
              </label>
              {prof.business_logo && <button data-testid="invoice-logo-remove" onClick={() => set({ business_logo: "" })} className="text-xs text-[var(--text-2)] hover:text-[var(--danger)]">Remove</button>}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="biz-name" value={prof.business_name || ""} onChange={(e) => set({ business_name: e.target.value })} placeholder="Business name" className="h-10 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
            <input data-testid="biz-email" value={prof.business_email || ""} onChange={(e) => set({ business_email: e.target.value })} placeholder="Contact email" className="h-10 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
            <input data-testid="biz-phone" value={prof.business_phone || ""} onChange={(e) => set({ business_phone: e.target.value })} placeholder="Phone" className="h-10 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
            <input data-testid="biz-address" value={prof.business_address || ""} onChange={(e) => set({ business_address: e.target.value })} placeholder="Address" className="h-10 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
          </div>
          <div className="flex justify-end">
            <button data-testid="biz-save" onClick={save} disabled={saving} className="h-10 px-5 bg-[#1A1A18] text-white text-sm hover:bg-[var(--accent)] transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save details"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function LineItemModal({ open, onClose, templates, reloadTemplates, onAdd }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState("");
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [tplId, setTplId] = useState("");

  useEffect(() => {
    if (open) { setName(""); setCode(""); setDescription(""); setQty(1); setPrice(""); setSaveAsTemplate(false); setTplId(""); }
  }, [open]);

  if (!open) return null;

  const pickTemplate = (id) => {
    setTplId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setName(t.name || ""); setCode(t.service_code || ""); setDescription(t.description || ""); setPrice(String(t.price ?? "")); }
  };

  const add = async () => {
    const unit = parseFloat(price) || 0;
    const desc = [name.trim(), description.trim()].filter(Boolean).join(" - ");
    if (!desc) return toast.error("Enter a name or description");
    if (saveAsTemplate) {
      if (!name.trim()) return toast.error("Give the item a name to save it as a template");
      try { await api.post("/line-item-templates", { name: name.trim(), description: description.trim(), price: unit, service_code: code.trim() }); reloadTemplates(); toast.success("Saved to your library"); }
      catch (e) { toast.error("Could not save template"); }
    }
    onAdd({ service_code: code.trim(), description: desc, qty: Number(qty) || 1, unit_price: unit });
    onClose();
  };

  return (
    <div data-testid="line-item-modal" className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-[var(--line)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--line)]">
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><BookMarked className="w-4 h-4" /> Add line item</p>
          <button data-testid="line-item-modal-close" onClick={onClose} className="text-[var(--text-2)] hover:text-[var(--danger)]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--text-2)]">Choose a saved item</label>
            <select data-testid="line-item-template-select" value={tplId} onChange={(e) => pickTemplate(e.target.value)} className="mt-1 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]">
              <option value="">— Type a new item below —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {money(t.price)}</option>)}
            </select>
          </div>
          <input data-testid="line-item-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Service code (e.g. GF-SOD-T1)" className="w-full h-11 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
          <input data-testid="line-item-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Item name" className="w-full h-11 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
          <textarea data-testid="line-item-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" rows={4} className="w-full px-3 py-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)] resize-y" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[var(--text-2)]">Qty</label>
              <input data-testid="line-item-qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 w-full h-11 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[var(--text-2)]">Unit price $</label>
              <input data-testid="line-item-price" type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" className="mt-1 w-full h-11 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]" />
            </div>
          </div>
          <label data-testid="line-item-save-template" className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} className="w-4 h-4 accent-[var(--accent)]" />
            <span className="text-sm text-[var(--text-2)]">Save as template for future invoices</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-[var(--line)]">
          <button data-testid="line-item-cancel" onClick={onClose} className="h-10 px-4 border border-[var(--line)] text-sm hover:border-[var(--accent)] transition-colors">Cancel</button>
          <button data-testid="line-item-add" onClick={add} className="h-10 px-5 bg-[#1A1A18] text-white text-sm hover:bg-[var(--accent)] transition-colors flex items-center gap-2"><Plus className="w-4 h-4" /> Add to invoice</button>
        </div>
      </div>
    </div>
  );
}

function SavedItemsLibrary({ templates, reload, onInsert }) {
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({ name: "", service_code: "", description: "", price: "" });

  const startEdit = (t) => { setEditId(t.id); setDraft({ name: t.name || "", service_code: t.service_code || "", description: t.description || "", price: String(t.price ?? "") }); };
  const saveEdit = async (id) => {
    if (!draft.name.trim()) return toast.error("Name is required");
    try { await api.put(`/line-item-templates/${id}`, { name: draft.name.trim(), service_code: draft.service_code.trim(), description: draft.description.trim(), price: parseFloat(draft.price) || 0 }); setEditId(null); reload(); toast.success("Template updated"); }
    catch (e) { toast.error("Could not update template"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this saved item?")) return;
    try { await api.delete(`/line-item-templates/${id}`); reload(); } catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="saved-items-library" className="mt-3 border border-[var(--line)] bg-[#f9f9f7] p-4">
      <p className="label-mono text-[var(--text-2)] mb-3 flex items-center gap-2"><BookMarked className="w-3.5 h-3.5" /> Saved line items ({templates.length})</p>
      {templates.length === 0 ? (
        <p className="text-xs text-[var(--text-2)]">No saved items yet. Tick “Save as template” when adding a line item.</p>
      ) : (
        <ul className="space-y-2">
          {templates.map((t) => (
            <li key={t.id} data-testid="saved-item-row" className="bg-white border border-[var(--line)] p-3">
              {editId === t.id ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input data-testid="saved-item-edit-name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Name" className="flex-1 h-9 px-2 border border-[var(--line)] text-sm outline-none focus:border-[var(--accent)]" />
                    <input data-testid="saved-item-edit-code" value={draft.service_code} onChange={(e) => setDraft((d) => ({ ...d, service_code: e.target.value }))} placeholder="Code" className="w-32 h-9 px-2 border border-[var(--line)] text-sm outline-none focus:border-[var(--accent)]" />
                    <input data-testid="saved-item-edit-price" type="number" step="0.01" value={draft.price} onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))} placeholder="Price" className="w-28 h-9 px-2 border border-[var(--line)] text-sm outline-none focus:border-[var(--accent)]" />
                  </div>
                  <textarea data-testid="saved-item-edit-desc" value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} rows={3} placeholder="Description" className="w-full px-2 py-1.5 border border-[var(--line)] text-sm outline-none focus:border-[var(--accent)] resize-y" />
                  <div className="flex justify-end gap-2">
                    <button data-testid="saved-item-edit-cancel" onClick={() => setEditId(null)} className="h-8 px-3 border border-[var(--line)] text-xs hover:border-[var(--accent)] transition-colors">Cancel</button>
                    <button data-testid="saved-item-edit-save" onClick={() => saveEdit(t.id)} className="h-8 px-3 bg-[#1A1A18] text-white text-xs hover:bg-[var(--accent)] transition-colors flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Save</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium flex items-center gap-2 flex-wrap">{t.service_code && <span className="font-mono text-[11px] px-1.5 py-0.5 bg-[var(--accent)]/10 text-[var(--accent)] rounded">{t.service_code}</span>}{t.name} <span className="font-mono text-[var(--accent)]">{money(t.price)}</span></p>
                    {t.description && <p className="text-xs text-[var(--text-2)] mt-0.5 line-clamp-2">{t.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid="saved-item-insert" onClick={() => onInsert({ service_code: t.service_code || "", description: [t.name, t.description].filter(Boolean).join(" - "), qty: 1, unit_price: t.price })} title="Add to invoice" className="h-8 px-2 border border-[var(--line)] text-xs hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Insert</button>
                    <button data-testid="saved-item-edit" onClick={() => startEdit(t)} className="w-8 h-8 flex items-center justify-center border border-[var(--line)] hover:border-[var(--accent)] transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid="saved-item-delete" onClick={() => del(t.id)} className="w-8 h-8 flex items-center justify-center border border-[var(--line)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InvoiceActivity({ inv }) {
  const sent = (inv.reminders_sent || []).slice().sort((a, b) => a - b);
  if (!inv.viewed_at && sent.length === 0) return null;
  return (
    <p data-testid={`invoice-activity-${inv.id}`} className="text-[11px] text-[var(--text-2)] flex flex-wrap items-center gap-2 mt-0.5">
      {inv.viewed_at && <span className="flex items-center gap-1 text-[#1d4ed8]"><Eye className="w-3 h-3" /> Viewed {String(inv.viewed_at).slice(0, 10)}{inv.view_count > 1 ? ` · ${inv.view_count}×` : ""}</span>}
      {sent.length > 0 && <span className="flex items-center gap-1"><Bell className="w-3 h-3" /> Reminders sent: {sent.map((d) => `${d}d`).join(", ")}</span>}
    </p>
  );
}


function TotalsBlock({ total, downPayment, setDownPayment, dpAmt, balanceDue }) {
  return (
    <div data-testid="invoice-totals" className="ml-auto w-full sm:w-72 text-sm space-y-1.5">
      <div className="flex justify-between"><span className="text-[var(--text-2)]">Subtotal</span><span data-testid="invoice-total" className="font-mono">{money(total)}</span></div>
      <div className="flex justify-between items-center gap-2">
        <span className="text-[var(--text-2)]">Down Payment</span>
        <span className="flex items-center border border-[var(--line)] focus-within:border-[var(--accent)] h-8 px-2 w-28"><span className="text-[var(--text-2)] text-xs">$</span>
          <input data-testid="invoice-down-payment" type="number" min="0" step="0.01" value={downPayment} onChange={(e) => setDownPayment(e.target.value)} placeholder="0.00" className="w-full bg-transparent text-right font-mono outline-none" /></span>
      </div>
      {dpAmt > 0 && <div data-testid="invoice-dp-applied" className="flex justify-between text-[#0d7d72]"><span>Down Payment Applied</span><span className="font-mono">-{money(dpAmt)}</span></div>}
      <div className="flex justify-between border-t border-[var(--line)] pt-1.5"><span className="font-semibold">{dpAmt > 0 ? "Balance Due" : "Total"}</span><span data-testid="invoice-balance-due" className="font-mono font-bold text-lg">{money(balanceDue)}</span></div>
    </div>
  );
}

function LineSummary({ inv }) {
  const text = (inv.line_items || []).map((l) => [l.service_code, l.description].filter(Boolean).join(" · ")).filter(Boolean).join(" | ");
  if (!text) return null;
  return <p data-testid={`invoice-lines-${inv.id}`} className="text-[11px] text-[var(--text-2)] mt-0.5 line-clamp-2 break-words whitespace-pre-wrap">{text}</p>;
}

function CreditsBar({ credits }) {
  const [buying, setBuying] = useState(null);
  if (!credits || credits.unlimited) return null;
  const buy = async (pack) => { setBuying(pack); if (!(await buyCredits(pack))) setBuying(null); };
  const n = credits.credits;
  const tone = n === 0 ? "border-[var(--danger)] bg-[var(--danger)]/5" : n === 1 ? "border-[#946200] bg-[#FFC43D]/15" : "border-[var(--line)] bg-white";
  return (
    <div data-testid="invoice-credits-bar" className={`border p-4 flex flex-wrap items-center justify-between gap-3 ${tone}`}>
      <div>
        {n <= 1 && <p data-testid="invoice-credits-warning" className={`text-xs font-semibold uppercase tracking-wider mb-1 flex items-center gap-1 ${n === 0 ? "text-[var(--danger)]" : "text-[#946200]"}`}><AlertTriangle className="w-3.5 h-3.5" /> {n === 0 ? "Out of credits — top up to create invoices" : "Heads up: last invoice credit"}</p>}
        <p className="text-sm font-medium"><span data-testid="invoice-credits-count" className="font-mono text-lg mr-1">{n}</span> invoice credit{n === 1 ? "" : "s"} left</p>
        <p className="text-xs text-[var(--text-2)]">1 credit = 1 invoice. Estimates are free. Business Platinum = unlimited.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {credits.packs.map((p) => (
          <button key={p.id} data-testid={`buy-credits-${p.id}`} onClick={() => buy(p.id)} disabled={!!buying} className="h-10 px-4 border border-[var(--accent)] text-[var(--accent)] text-sm hover:bg-[var(--accent)] hover:text-white transition-colors disabled:opacity-50">
            {buying === p.id ? "Opening…" : `${n <= 1 ? "Top up " : ""}${p.credits} credits · $${p.price.toFixed(2)}`}
          </button>
        ))}
      </div>
    </div>
  );
}


const FREQ_LABEL = { weekly: "weekly", biweekly: "every 2 weeks", monthly: "monthly", quarterly: "quarterly" };

function RecurringBadge({ inv, onToggle }) {
  if (inv.recur_parent) {
    return <p data-testid={`invoice-recur-child-${inv.id}`} className="text-[11px] text-[var(--text-2)] flex items-center gap-1 mt-0.5"><Repeat className="w-3 h-3" /> Auto-generated from a recurring invoice</p>;
  }
  if (!FREQ_LABEL[inv.recurrence]) return null;
  return (
    <p data-testid={`invoice-recur-${inv.id}`} className="text-[11px] flex flex-wrap items-center gap-2 mt-0.5">
      <span className={`flex items-center gap-1 ${inv.recur_active ? "text-[var(--accent)]" : "text-[var(--text-2)]"}`}>
        <Repeat className="w-3 h-3" /> Repeats {FREQ_LABEL[inv.recurrence]}{inv.recur_active ? ` · next ${inv.recur_next_date}` : " · stopped"}
      </span>
      <button data-testid={`invoice-recur-toggle-${inv.id}`} onClick={onToggle} className="underline text-[var(--text-2)] hover:text-[var(--accent)]">{inv.recur_active ? "Stop" : "Resume"}</button>
    </p>
  );
}
